import { oc } from "@orpc/contract";
import { z } from "zod";
import { WorkflowRunResponseSchema } from "../workflows.contract";

const UUIDSchema = z.uuid();

/**
 * The matchable fields on an inbound email. `from`/`to`/`cc`/`bcc`/`mailbox`
 * are address lists; `subject` is free text. Matching is always substring,
 * case-insensitive.
 */
export const EmailRuleConditionFieldSchema = z.enum([
  "from",
  "to",
  "cc",
  "bcc",
  "subject",
  "mailbox",
]);

export const EmailRuleConditionMatchTypeSchema = z.enum([
  "contains",
  "notContains",
]);

export const EmailRuleBooleanOperatorSchema = z.enum(["and", "or"]);

/** A single field test, e.g. `cc contains "aurora@"`. */
export const EmailRuleConditionSchema = z.object({
  field: EmailRuleConditionFieldSchema,
  matchType: EmailRuleConditionMatchTypeSchema,
  value: z.string(),
});

/**
 * A group of conditions combined by a single operator. `or` is what lets a
 * rule match "To OR Cc" by putting a `to` and a `cc` condition in one
 * `or` group.
 */
export const EmailRuleConditionGroupSchema = z.object({
  operator: EmailRuleBooleanOperatorSchema,
  conditions: z.array(EmailRuleConditionSchema),
});

/**
 * The current ("grouped") filter shape: a two-level boolean tree — a
 * top-level operator combines groups, and each group's operator combines its
 * conditions. `hasAttachments` is a separate tri-state always AND-ed with the
 * group result (null/undefined = don't care).
 */
export const GroupedEmailRuleFiltersSchema = z.object({
  operator: EmailRuleBooleanOperatorSchema,
  groups: z.array(EmailRuleConditionGroupSchema),
  hasAttachments: z.boolean().nullable().optional(),
});

/**
 * The legacy flat filter shape. Each field array is `(inc1 OR inc2 …) AND
 * NOT exc1 AND NOT exc2` (values prefixed with "!" are exclusions), and the
 * fields AND together. Retained so rules saved before grouped filters keep
 * matching; new/edited rules are stored grouped (see {@link toGroupedFilters}).
 */
export const LegacyEmailRuleFiltersSchema = z.object({
  from: z.array(z.string()).optional(),
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  bcc: z.array(z.string()).optional(),
  subject: z.array(z.string()).optional(),
  mailbox: z.array(z.string()).optional(),
  hasAttachments: z.boolean().nullable().optional(),
});

/**
 * A rule's stored filters: either the grouped shape (tried first) or the
 * legacy flat shape. Consumers should normalize to grouped with
 * {@link toGroupedFilters} rather than branching on the shape themselves.
 */
export const EmailRuleFiltersSchema = z.union([
  GroupedEmailRuleFiltersSchema,
  LegacyEmailRuleFiltersSchema,
]);

export const EmailRuleEventOptionSchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  description: z.string().nullable(),
  sortOrder: z.int(),
});

export const WorkflowNameSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

export const EmailRuleSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  ruleName: z.string(),
  enabled: z.boolean(),
  filters: EmailRuleFiltersSchema,
  events: z.array(EmailRuleEventOptionSchema),
  workflows: z.array(WorkflowNameSchema).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ListEmailRulesInputSchema = z.object({});
export const ListEmailRulesResponseSchema = z.object({
  rules: z.array(EmailRuleSchema),
});

export const GetEmailRuleInputSchema = z.object({
  id: UUIDSchema,
});
export const GetEmailRuleResponseSchema = z.object({
  rule: EmailRuleSchema,
});

export const CreateEmailRuleInputSchema = z.object({
  ruleName: z.string().min(1),
  enabled: z.boolean().optional(),
  filters: EmailRuleFiltersSchema.optional(),
  eventOptionIds: z.array(UUIDSchema).optional(),
});
export const CreateEmailRuleResponseSchema = z.object({
  rule: EmailRuleSchema,
});

export const UpdateEmailRuleInputSchema = z.object({
  id: UUIDSchema,
  ruleName: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  filters: EmailRuleFiltersSchema.optional(),
  eventOptionIds: z.array(UUIDSchema).optional(),
});
export const UpdateEmailRuleResponseSchema = z.object({
  rule: EmailRuleSchema,
});

export const DeleteEmailRuleInputSchema = z.object({
  id: UUIDSchema,
});
export const DeleteEmailRuleResponseSchema = z.object({
  success: z.boolean(),
});

export const TestInboundEmailInputSchema = z.object({
  from: z
    .string()
    .nullable()
    .optional()
    .describe("Single email address; matched via substring"),
  to: z
    .string()
    .nullable()
    .optional()
    .describe("Comma-separated email addresses"),
  cc: z
    .string()
    .nullable()
    .optional()
    .describe("Comma-separated email addresses"),
  bcc: z
    .string()
    .nullable()
    .optional()
    .describe("Comma-separated email addresses"),
  subject: z
    .string()
    .nullable()
    .optional()
    .describe("Email subject; matched via substring"),
  mailbox: z
    .string()
    .nullable()
    .optional()
    .describe("Receiving mailbox UPN; matched via substring"),
  hasAttachments: z
    .boolean()
    .nullable()
    .optional()
    .describe("Whether the email has attachments"),
});
export const TestInboundEmailResponseSchema = z.object({
  matchingRules: z.array(EmailRuleSchema),
});

export const PreviewInboundEmailWorkflowsInputSchema = z.object({
  inboundEmailId: UUIDSchema,
});
export const PreviewInboundEmailWorkflowsResponseSchema = z.object({
  matchingRules: z.array(EmailRuleSchema),
  workflows: z.array(WorkflowNameSchema),
});

export const RerunInboundEmailWorkflowsInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  emailRuleOptionIds: z
    .array(UUIDSchema)
    .optional()
    .describe(
      "Optional filter to only trigger specific email rule events. If not provided, triggers all matching rule events.",
    ),
  forceFreshExtraction: z
    .boolean()
    .optional()
    .describe(
      "Re-extract the email's documents instead of reusing their completed OCR results. Default false: the re-run reuses cached extractions.",
    ),
});
export const RerunInboundEmailWorkflowsResponseSchema = z.object({
  success: z.boolean(),
  publishedEvents: z.int(),
});

export const ListWorkflowsTriggerableByInboundEmailInputSchema = z.object({
  inboundEmailId: UUIDSchema,
});
export const ListWorkflowsTriggerableByInboundEmailResponseSchema = z.object({
  workflows: z.array(WorkflowNameSchema),
});

export const GetInboundEmailWorkflowRunSummaryInputSchema = z.object({
  inboundEmailIds: z.array(UUIDSchema).min(1),
});

export const InboundEmailWorkflowRunSummarySchema = z.object({
  hasAny: z.boolean(),
  topLevelRunCount: z.number(),
  childRunCount: z.number(),
});

export const GetInboundEmailWorkflowRunSummaryResponseSchema = z.object({
  summaryByInboundEmailId: z.record(
    UUIDSchema,
    InboundEmailWorkflowRunSummarySchema,
  ),
});

export const ListInboundEmailWorkflowRunsInputSchema = z.object({
  inboundEmailId: UUIDSchema,
});

export const InboundEmailWorkflowRunSchema = z.object({
  run: WorkflowRunResponseSchema,
  workflowDefinitionName: z.string(),
});

export const ListInboundEmailWorkflowRunsResponseSchema = z.object({
  runs: z.array(InboundEmailWorkflowRunSchema),
});

export const CreateEmailRuleEventAndLinkInputSchema = z.object({
  ruleId: UUIDSchema,
  name: z.string().min(1),
  description: z.string().optional(),
  sortOrder: z.int().optional(),
});
export const CreateEmailRuleEventAndLinkResponseSchema = z.object({
  rule: EmailRuleSchema,
});

export const UpdateEmailRuleEventInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  sortOrder: z.int().optional(),
});
export const UpdateEmailRuleEventResponseSchema = z.object({
  event: EmailRuleEventOptionSchema,
});

export const DeleteEmailRuleEventInputSchema = z.object({
  id: UUIDSchema,
});
export const DeleteEmailRuleEventResponseSchema = z.object({
  success: z.boolean(),
});

export const emailRulesContracts = {
  list: oc
    .input(ListEmailRulesInputSchema)
    .output(ListEmailRulesResponseSchema)
    .route({
      method: "GET",
      summary: "List email rules",
      description:
        "Lists the organization's email routing rules with their sender, recipient, subject, mailbox, and attachment conditions, enabled flag, events, and the workflows each triggers.",
    }),
  get: oc
    .input(GetEmailRuleInputSchema)
    .output(GetEmailRuleResponseSchema)
    .route({
      method: "GET",
      summary: "Get an email rule",
      description:
        "Returns one email routing rule with its match conditions, enabled flag, and events.",
    }),
  create: oc
    .input(CreateEmailRuleInputSchema)
    .output(CreateEmailRuleResponseSchema)
    .route({
      method: "POST",
      summary: "Create an email rule",
      description:
        "Creates an email routing rule that matches incoming email by sender, recipient, CC, BCC, subject, mailbox, or attachments, and links events that trigger workflows.",
    }),
  update: oc
    .input(UpdateEmailRuleInputSchema)
    .output(UpdateEmailRuleResponseSchema)
    .route({
      method: "POST",
      summary: "Update an email rule",
      description:
        "Changes an email rule's name, enabled flag, match conditions, or linked events. Affects which workflows future incoming emails trigger.",
    }),
  delete: oc
    .input(DeleteEmailRuleInputSchema)
    .output(DeleteEmailRuleResponseSchema)
    .route({
      method: "POST",
      summary: "Delete an email rule",
      description:
        "Deletes an email routing rule so incoming emails no longer trigger workflows through it.",
    }),
  testInbound: oc
    .input(TestInboundEmailInputSchema)
    .output(TestInboundEmailResponseSchema)
    .route({
      method: "POST",
      summary: "Test which email rules match an email",
      description:
        "Returns the email rules that a sample email (sender, recipients, subject, mailbox, attachments) would match. Changes nothing.",
    }),
  previewInboundEmailWorkflows: oc
    .input(PreviewInboundEmailWorkflowsInputSchema)
    .output(PreviewInboundEmailWorkflowsResponseSchema)
    .route({
      method: "GET",
      summary: "Preview workflows for a received email",
      description:
        "Returns the email rules a received email matches and the workflows they would trigger. Changes nothing.",
    }),
  rerunInboundEmailWorkflows: oc
    .input(RerunInboundEmailWorkflowsInputSchema)
    .output(RerunInboundEmailWorkflowsResponseSchema)
    .route({
      method: "POST",
      summary: "Re-run workflows for a received email",
      description:
        "Re-triggers the workflows for a received email's matching email rules, optionally limited to specific rule events. Returns the number of events published.",
    }),
  listWorkflowsTriggerableByInboundEmail: oc
    .input(ListWorkflowsTriggerableByInboundEmailInputSchema)
    .output(ListWorkflowsTriggerableByInboundEmailResponseSchema)
    .route({
      method: "GET",
      summary: "List workflows a received email can trigger",
      description:
        "Lists the workflows that a received email would trigger through the email rules it matches.",
    }),
  getInboundEmailWorkflowRunSummary: oc
    .input(GetInboundEmailWorkflowRunSummaryInputSchema)
    .output(GetInboundEmailWorkflowRunSummaryResponseSchema)
    .route({
      method: "POST",
      summary: "Summarize workflow runs for received emails",
      description:
        "For each given received email, returns whether any email-rule workflow runs exist and counts of top-level and child runs.",
    }),
  listInboundEmailWorkflowRuns: oc
    .input(ListInboundEmailWorkflowRunsInputSchema)
    .output(ListInboundEmailWorkflowRunsResponseSchema)
    .route({
      method: "GET",
      summary: "List workflow runs for a received email",
      description:
        "Lists the workflow runs a received email triggered through email rules, with workflow names, showing child runs when they exist.",
    }),
  events: {
    createAndLink: oc
      .input(CreateEmailRuleEventAndLinkInputSchema)
      .output(CreateEmailRuleEventAndLinkResponseSchema)
      .route({
        method: "POST",
        summary: "Add an event to an email rule",
        description:
          "Creates a named event and links it to an email rule, so workflows listening for that event run when the rule matches.",
      }),
    update: oc
      .input(UpdateEmailRuleEventInputSchema)
      .output(UpdateEmailRuleEventResponseSchema)
      .route({
        method: "POST",
        summary: "Update an email rule event",
        description:
          "Renames or re-describes an email rule event, or changes its sort order.",
      }),
    delete: oc
      .input(DeleteEmailRuleEventInputSchema)
      .output(DeleteEmailRuleEventResponseSchema)
      .route({
        method: "POST",
        summary: "Delete an email rule event",
        description:
          "Deletes an email rule event, so workflows listening for it are no longer triggered by email rules.",
      }),
  },
} as const;

export type EmailRuleFilters = z.infer<typeof EmailRuleFiltersSchema>;
export type GroupedEmailRuleFilters = z.infer<
  typeof GroupedEmailRuleFiltersSchema
>;
export type LegacyEmailRuleFilters = z.infer<
  typeof LegacyEmailRuleFiltersSchema
>;
export type EmailRuleCondition = z.infer<typeof EmailRuleConditionSchema>;
export type EmailRuleConditionGroup = z.infer<
  typeof EmailRuleConditionGroupSchema
>;
export type EmailRuleConditionField = z.infer<
  typeof EmailRuleConditionFieldSchema
>;
export type EmailRuleConditionMatchType = z.infer<
  typeof EmailRuleConditionMatchTypeSchema
>;
export type EmailRuleBooleanOperator = z.infer<
  typeof EmailRuleBooleanOperatorSchema
>;

const LEGACY_FILTER_FIELDS = [
  "from",
  "to",
  "cc",
  "bcc",
  "subject",
  "mailbox",
] as const;

/** True when the filters are already in the grouped shape. */
function isGroupedFilters(
  filters: EmailRuleFilters,
): filters is GroupedEmailRuleFilters {
  return (
    "groups" in filters &&
    Array.isArray((filters as GroupedEmailRuleFilters).groups)
  );
}

/**
 * Normalizes a rule's filters to the canonical grouped shape so every
 * consumer (matcher, editor, summaries) evaluates one representation.
 *
 * Grouped filters pass through. Legacy filters are converted preserving exact
 * semantics: per field, the includes become one `or` group of `contains`
 * conditions and each exclude becomes a single-condition `notContains` group;
 * all groups AND together at the top level (matching the legacy
 * `(inc OR …) AND NOT exc AND fieldB…` behavior). `hasAttachments` carries
 * through unchanged.
 */
export function toGroupedFilters(
  filters?: EmailRuleFilters | null,
): GroupedEmailRuleFilters {
  if (!filters) {
    return { operator: "and", groups: [], hasAttachments: null };
  }

  if (isGroupedFilters(filters)) {
    return {
      operator: filters.operator ?? "and",
      groups: filters.groups ?? [],
      hasAttachments: filters.hasAttachments ?? null,
    };
  }

  const groups: EmailRuleConditionGroup[] = [];
  for (const field of LEGACY_FILTER_FIELDS) {
    const values = filters[field] ?? [];
    const includes = values.filter((v) => !v.startsWith("!"));
    const excludes = values
      .filter((v) => v.startsWith("!"))
      .map((v) => v.slice(1).trim())
      .filter((v) => v.length > 0);

    if (includes.length > 0) {
      groups.push({
        operator: "or",
        conditions: includes.map((value) => ({
          field,
          matchType: "contains",
          value,
        })),
      });
    }
    for (const value of excludes) {
      groups.push({
        operator: "and",
        conditions: [{ field, matchType: "notContains", value }],
      });
    }
  }

  return {
    operator: "and",
    groups,
    hasAttachments: filters.hasAttachments ?? null,
  };
}

export type EmailRuleEventOption = z.infer<typeof EmailRuleEventOptionSchema>;
export type EmailRule = z.infer<typeof EmailRuleSchema>;

export type ListEmailRulesInput = z.infer<typeof ListEmailRulesInputSchema>;
export type ListEmailRulesResponse = z.infer<
  typeof ListEmailRulesResponseSchema
>;

export type GetEmailRuleInput = z.infer<typeof GetEmailRuleInputSchema>;
export type GetEmailRuleResponse = z.infer<typeof GetEmailRuleResponseSchema>;

export type CreateEmailRuleInput = z.infer<typeof CreateEmailRuleInputSchema>;
export type CreateEmailRuleResponse = z.infer<
  typeof CreateEmailRuleResponseSchema
>;

export type UpdateEmailRuleInput = z.infer<typeof UpdateEmailRuleInputSchema>;
export type UpdateEmailRuleResponse = z.infer<
  typeof UpdateEmailRuleResponseSchema
>;

export type DeleteEmailRuleInput = z.infer<typeof DeleteEmailRuleInputSchema>;
export type DeleteEmailRuleResponse = z.infer<
  typeof DeleteEmailRuleResponseSchema
>;

export type TestInboundEmailInput = z.infer<typeof TestInboundEmailInputSchema>;
export type TestInboundEmailResponse = z.infer<
  typeof TestInboundEmailResponseSchema
>;

export type WorkflowName = z.infer<typeof WorkflowNameSchema>;

export type PreviewInboundEmailWorkflowsInput = z.infer<
  typeof PreviewInboundEmailWorkflowsInputSchema
>;
export type PreviewInboundEmailWorkflowsResponse = z.infer<
  typeof PreviewInboundEmailWorkflowsResponseSchema
>;

export type RerunInboundEmailWorkflowsInput = z.infer<
  typeof RerunInboundEmailWorkflowsInputSchema
>;
export type RerunInboundEmailWorkflowsResponse = z.infer<
  typeof RerunInboundEmailWorkflowsResponseSchema
>;

export type ListWorkflowsTriggerableByInboundEmailInput = z.infer<
  typeof ListWorkflowsTriggerableByInboundEmailInputSchema
>;
export type ListWorkflowsTriggerableByInboundEmailResponse = z.infer<
  typeof ListWorkflowsTriggerableByInboundEmailResponseSchema
>;

export type GetInboundEmailWorkflowRunSummaryInput = z.infer<
  typeof GetInboundEmailWorkflowRunSummaryInputSchema
>;
export type InboundEmailWorkflowRunSummary = z.infer<
  typeof InboundEmailWorkflowRunSummarySchema
>;
export type GetInboundEmailWorkflowRunSummaryResponse = z.infer<
  typeof GetInboundEmailWorkflowRunSummaryResponseSchema
>;

export type ListInboundEmailWorkflowRunsInput = z.infer<
  typeof ListInboundEmailWorkflowRunsInputSchema
>;
export type InboundEmailWorkflowRun = z.infer<
  typeof InboundEmailWorkflowRunSchema
>;
export type ListInboundEmailWorkflowRunsResponse = z.infer<
  typeof ListInboundEmailWorkflowRunsResponseSchema
>;

export type CreateEmailRuleEventAndLinkInput = z.infer<
  typeof CreateEmailRuleEventAndLinkInputSchema
>;
export type CreateEmailRuleEventAndLinkResponse = z.infer<
  typeof CreateEmailRuleEventAndLinkResponseSchema
>;

export type UpdateEmailRuleEventInput = z.infer<
  typeof UpdateEmailRuleEventInputSchema
>;
export type UpdateEmailRuleEventResponse = z.infer<
  typeof UpdateEmailRuleEventResponseSchema
>;

export type DeleteEmailRuleEventInput = z.infer<
  typeof DeleteEmailRuleEventInputSchema
>;
export type DeleteEmailRuleEventResponse = z.infer<
  typeof DeleteEmailRuleEventResponseSchema
>;
